import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faCalendar,
  faGraduationCap,
  faSpinner,
  faExclamationCircle,
  faChevronRight,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

interface EnrollmentSummary {
  enrollmentId: string;
  learnershipId: string;
  title: string;
  saqaId: string;
  nqfLevel: number | null;
  credits: number | null;
  durationMonths: number;
  enrollmentDate: string;
  completionDate: string | null;
  status: number;
  overallProgressPercent: number;
  completedModules: number;
  totalModules: number;
}

const statusMap: { [key: number]: { label: string; className: string } } = {
  0: { label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  1: { label: 'Completed', className: 'bg-green-100 text-green-700' },
  2: { label: 'Withdrawn', className: 'bg-gray-100 text-gray-700' },
  3: { label: 'On Hold', className: 'bg-yellow-100 text-yellow-700' },
};

export default function MyLearnershipsPage() {
  const [enrollments, setEnrollments] = useState<EnrollmentSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/learnerships/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to load learnerships');

      const data = await response.json();
      setEnrollments(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load your learnerships. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">My Learnerships</h1>
        <p className="text-sm text-brand-900/60 mt-1">
          Track your enrolled programmes and module progress
        </p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && enrollments.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faLayerGroup} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No learnerships yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              You're not enrolled in any learnerships yet. Contact your administrator to get started.
            </p>
          </div>
        )}

        {!isLoading && !error && enrollments.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {enrollments.map((enrollment) => {
              const status = statusMap[enrollment.status] ?? statusMap[0];
              return (
                <div key={enrollment.enrollmentId} className="card-surface p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-2xl bg-brand-gradient flex items-center justify-center text-white flex-shrink-0">
                        <FontAwesomeIcon icon={faBook} className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="font-bold text-brand-950">{enrollment.title}</h3>
                        <p className="text-xs text-brand-900/50 mt-1">
                          SAQA {enrollment.saqaId}
                          {enrollment.nqfLevel ? ` · NQF ${enrollment.nqfLevel}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${status.className}`}>
                      {status.label}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="mt-5">
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="text-brand-900/60">Overall progress</span>
                      <span className="font-semibold text-brand-700">
                        {enrollment.overallProgressPercent}%
                      </span>
                    </div>
                    <div className="w-full bg-canvas-200 rounded-full h-2">
                      <div
                        className="bg-brand-gradient h-2 rounded-full transition-all"
                        style={{ width: `${enrollment.overallProgressPercent}%` }}
                      />
                    </div>
                    <p className="text-xs text-brand-900/50 mt-1.5">
                      {enrollment.completedModules} of {enrollment.totalModules} modules completed
                    </p>
                  </div>

                  {/* Meta info */}
                  <div className="mt-5 grid grid-cols-3 gap-2 border-t border-canvas-200 pt-4 text-xs">
                    <div>
                      <p className="text-brand-900/45 flex items-center gap-1">
                        <FontAwesomeIcon icon={faCalendar} className="h-3 w-3" /> Enrolled
                      </p>
                      <p className="font-semibold text-brand-950 mt-1">
                        {new Date(enrollment.enrollmentDate).toLocaleDateString('en-ZA', {
                          month: 'short',
                          year: 'numeric',
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-brand-900/45 flex items-center gap-1">
                        <FontAwesomeIcon icon={faGraduationCap} className="h-3 w-3" /> Credits
                      </p>
                      <p className="font-semibold text-brand-950 mt-1">
                        {enrollment.credits ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-brand-900/45">Duration</p>
                      <p className="font-semibold text-brand-950 mt-1">
                        {enrollment.durationMonths} mo
                      </p>
                    </div>
                  </div>

                  <button className="w-full mt-5 flex items-center justify-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors">
                    View module breakdown
                    <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
